/**
 * Safe wrappers around window.localStorage / window.sessionStorage.
 *
 * Accessing storage can throw synchronously in some environments — e.g.
 * Safari private mode on older iOS versions, or Android webviews where
 * cookies/site data are blocked. In those cases even reading
 * `window.localStorage` throws a SecurityError, which would otherwise be
 * fatal at module load. These helpers turn every failure into a no-op
 * (writes) or `null` (reads) so the app degrades gracefully instead of
 * white-screening.
 */

const getStorage = (kind) => {
	try {
		return window[kind];
	} catch (error) {
		return null;
	}
};

const getItem = (kind, key) => {
	try {
		const storage = getStorage(kind);
		return storage ? storage.getItem(key) : null;
	} catch (error) {
		return null;
	}
};

const setItem = (kind, key, value) => {
	try {
		const storage = getStorage(kind);
		if (storage) {
			storage.setItem(key, value);
		}
	} catch (error) {
		// Storage unavailable or quota exceeded — ignore.
	}
};

const removeItem = (kind, key) => {
	try {
		const storage = getStorage(kind);
		if (storage) {
			storage.removeItem(key);
		}
	} catch (error) {
		// Storage unavailable — ignore.
	}
};

export const getLocalStorageItem = (key) => getItem('localStorage', key);
export const setLocalStorageItem = (key, value) => setItem('localStorage', key, value);
export const removeLocalStorageItem = (key) => removeItem('localStorage', key);

export const getSessionStorageItem = (key) => getItem('sessionStorage', key);
export const setSessionStorageItem = (key, value) => setItem('sessionStorage', key, value);
export const removeSessionStorageItem = (key) => removeItem('sessionStorage', key);
